import { useState, useEffect } from 'react';
import { useBroker } from '../contexts/BrokerContext';
import { useLocale } from '../contexts/LocaleContext';
import {
  Search, DollarSign, TrendingUp, TrendingDown, Lock, Settings,
  ChevronDown, SlidersHorizontal, X, AlertCircle, UserCheck, UserX, Activity
} from 'lucide-react';
import toast from 'react-hot-toast';

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

// ─── Account type badge style ─────────────────────────────────────────────────
function typeStyle(type) {
  return type === 'live'
    ? { bg: 'var(--accent-success)', color: 'var(--accent-success)' }
    : { bg: 'var(--accent-info)',    color: 'var(--accent-info)'    };
}

// ─── Mobile account card ──────────────────────────────────────────────────────
function AccountCard({ account, snapshot, onCreditDebit, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const ts = typeStyle(account.account_type);
  
  // Use real-time snapshot data if available, otherwise fall back to account data
  const balance = snapshot?.balance ?? account.balance;
  const equity = snapshot?.equity ?? account.equity;
  const margin_used = snapshot?.margin_used ?? 0;
  const free_margin = snapshot?.free_margin ?? balance;
  const margin_level = snapshot?.margin_level;
  
  // Calculate total P&L from positions
  const totalPnL = snapshot?.positions?.reduce((sum, pos) => sum + parseFloat(pos.unrealized_pnl || 0), 0) || 0;
  const positionsCount = snapshot?.positions?.length || 0;
  
  // Check if data is live
  const isLive = !!snapshot;

  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
    >
      {/* Main row */}
      <button className="w-full flex items-center gap-3 px-4 py-3.5 text-left" onClick={() => setExpanded(e => !e)}>
        {/* Avatar */}
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0 relative"
          style={{ backgroundColor: `color-mix(in srgb, ${ts.bg} 15%, transparent)`, color: ts.color }}
        >
          {account.account_type === 'live' ? 'L' : 'D'}
          {isLive && (
            <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          )}
        </div>

        {/* Account number + email */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black font-mono truncate" style={{ color: 'var(--text-primary)' }}>
            {account.account_number}
          </p>
          <p className="text-[11px] truncate" style={{ color: 'var(--text-tertiary)' }}>{account.email}</p>
        </div>

        {/* Balance/P&L + chevron */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <p className="text-sm font-black font-mono" style={{ color: 'var(--text-primary)' }}>
              ${parseFloat(balance || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </p>
            {isLive && positionsCount > 0 && (
              <p className="text-[10px] font-mono font-bold" style={{ 
                color: totalPnL >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' 
              }}>
                {totalPnL >= 0 ? '+' : ''}${totalPnL.toFixed(2)}
              </p>
            )}
          </div>
          <ChevronDown
            className="w-4 h-4 transition-transform duration-200"
            style={{ color: 'var(--text-tertiary)', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </div>
      </button>

      {/* Status badges row */}
      <div className="flex items-center gap-2 px-4 pb-3 flex-wrap">
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full capitalize"
          style={{
            backgroundColor: `color-mix(in srgb, ${ts.bg} 15%, transparent)`,
            color: ts.color,
            border: `1px solid color-mix(in srgb, ${ts.bg} 30%, transparent)`,
          }}
        >
          {account.account_type}
        </span>
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"
          style={{
            backgroundColor: account.is_active
              ? 'color-mix(in srgb, var(--accent-success) 15%, transparent)'
              : 'color-mix(in srgb, var(--text-tertiary) 15%, transparent)',
            color: account.is_active ? 'var(--accent-success)' : 'var(--text-tertiary)',
          }}
        >
          {account.is_active ? <UserCheck className="w-2.5 h-2.5" /> : <UserX className="w-2.5 h-2.5" />}
          {account.is_active ? 'Active' : 'Inactive'}
        </span>
        {Boolean(account.read_only) && (
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--accent-warning) 15%, transparent)',
              color: 'var(--accent-warning)',
            }}
          >
            <Lock className="w-2.5 h-2.5" /> Read-Only
          </span>
        )}
        {isLive && positionsCount > 0 && (
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--accent-success) 15%, transparent)',
              color: 'var(--accent-success)',
            }}
          >
            <Activity className="w-2.5 h-2.5" /> {positionsCount} {positionsCount === 1 ? 'Pos' : 'Pos'}
          </span>
        )}
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ backgroundColor: 'color-mix(in srgb, var(--text-tertiary) 15%, transparent)', color: 'var(--text-tertiary)' }}
        >
          {account.group_name || 'Standard'}
        </span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div
          className="border-t px-4 py-3 space-y-3"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'color-mix(in srgb, var(--bg-tertiary) 40%, transparent)' }}
        >
          {/* Detail rows */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Client</p>
              <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                {account.first_name} {account.last_name}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
                Equity {isLive && <Activity className="w-2 h-2" style={{ color: 'var(--accent-success)' }} />}
              </p>
              <p className="text-xs font-mono font-semibold" style={{ 
                color: equity && parseFloat(equity) >= parseFloat(balance) ? 'var(--accent-success)' : 'var(--accent-danger)' 
              }}>
                ${parseFloat(equity || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Balance</p>
              <p className="text-xs font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                ${parseFloat(balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            {isLive && positionsCount > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wide flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
                  P&L <Activity className="w-2 h-2" style={{ color: 'var(--accent-success)' }} />
                </p>
                <p className="text-xs font-mono font-bold" style={{ 
                  color: totalPnL >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' 
                }}>
                  {totalPnL >= 0 ? '+' : ''}${totalPnL.toFixed(2)}
                </p>
              </div>
            )}
            <div>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Free Margin</p>
              <p className="text-xs font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                ${parseFloat(free_margin || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            {margin_level && margin_used > 0 && (
              <>
                <div>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Margin Used</p>
                  <p className="text-xs font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                    ${parseFloat(margin_used).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Margin Level</p>
                  <p className="text-xs font-mono font-semibold" style={{ 
                    color: margin_level < 100 ? 'var(--accent-danger)' : margin_level < 200 ? 'var(--accent-warning)' : 'var(--accent-success)' 
                  }}>
                    {margin_level.toFixed(2)}%
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => onCreditDebit(account, 'credit')}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--accent-success) 12%, transparent)',
                color: 'var(--accent-success)',
                border: '1px solid color-mix(in srgb, var(--accent-success) 25%, transparent)',
              }}
            >
              <TrendingUp className="w-3.5 h-3.5" /> Credit
            </button>
            <button
              onClick={() => onCreditDebit(account, 'debit')}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)',
                color: 'var(--accent-danger)',
                border: '1px solid color-mix(in srgb, var(--accent-danger) 25%, transparent)',
              }}
            >
              <TrendingDown className="w-3.5 h-3.5" /> Debit
            </button>
            <button
              onClick={() => onEdit(account)}
              className="px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all active:scale-95"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)',
                color: 'var(--accent-primary)',
                border: '1px solid color-mix(in srgb, var(--accent-primary) 25%, transparent)',
              }}
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Mobile filter drawer ─────────────────────────────────────────────────────
function FilterDrawer({ filters, setFilters, searchTerm, setSearchTerm, onSearch, onClose, onClear, hasActive }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative rounded-t-2xl pt-4 pb-8 z-10 max-h-[80vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="flex items-center justify-between px-4 mb-5">
          <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Filters</h3>
          {hasActive && (
            <button onClick={onClear} className="text-xs font-semibold flex items-center gap-1"
              style={{ color: 'var(--accent-primary)' }}>
              <X className="w-3.5 h-3.5" />Clear all
            </button>
          )}
        </div>
        <div className="px-4 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-secondary)' }}>Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Account number or email…"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border focus:outline-none text-sm"
                style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-secondary)' }}>Account Type</label>
            <select
              value={filters.account_type}
              onChange={e => setFilters(f => ({ ...f, account_type: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl border focus:outline-none text-sm"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              <option value="">All Types</option>
              <option value="live">Live Only</option>
              <option value="demo">Demo Only</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-secondary)' }}>Status</label>
            <select
              value={filters.is_active === undefined ? '' : String(filters.is_active)}
              onChange={e => setFilters(f => ({ ...f, is_active: e.target.value === '' ? undefined : e.target.value === 'true' }))}
              className="w-full px-3 py-2.5 rounded-xl border focus:outline-none text-sm"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              <option value="">All Status</option>
              <option value="true">Active Only</option>
              <option value="false">Inactive Only</option>
            </select>
          </div>
          <button
            onClick={() => { onSearch(); onClose(); }}
            className="w-full py-3 rounded-xl text-sm font-bold text-white mt-1"
            style={{ backgroundColor: 'var(--accent-primary)' }}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Accounts() {
  const { t } = useLocale();
  const isMobile = useIsMobile();
  const { 
    fetchAccounts, 
    updateAccount, 
    creditAccount, 
    debitAccount, 
    fetchAccountGroups,
    accountSnapshots,
    wsConnected
  } = useBroker();

  const [accounts, setAccounts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ account_type: '', is_active: undefined });
  const [showFilters, setShowFilters] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => { loadData(); }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [accountsRes, groupsRes] = await Promise.all([
        fetchAccounts({ search: searchTerm, ...filters }),
        fetchAccountGroups(),
      ]);
      setAccounts(accountsRes.data.accounts || []);
      setGroups(groupsRes.data.groups || []);
    } catch {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => { e?.preventDefault(); loadData(); };

  const handleToggleActive = async (account) => {
    try {
      await updateAccount(account.id, { is_active: account.is_active ? 0 : 1 });
      toast.success(`Account ${account.is_active ? 'deactivated' : 'activated'}`);
      loadData();
    } catch { toast.error('Failed to update account status'); }
  };

  const handleToggleReadOnly = async (account) => {
    try {
      await updateAccount(account.id, { read_only: account.read_only ? 0 : 1 });
      toast.success(`Read-only mode ${account.read_only ? 'disabled' : 'enabled'}`);
      loadData();
    } catch { toast.error('Failed to update read-only mode'); }
  };

  const handleOpenCreditDebit = (account, type) => {
    setSelectedAccount({ ...account, type });
    setShowCreditModal(true);
  };

  const handleOpenEdit = (account) => {
    setSelectedAccount(account);
    setShowEditModal(true);
  };

  const filteredAccounts = accounts.filter(account => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return account.account_number?.toLowerCase().includes(s) || account.email?.toLowerCase().includes(s);
  });

  // Calculate stats with real-time data
  const stats = {
    total:        accounts.length,
    live:         accounts.filter(a => a.account_type === 'live').length,
    demo:         accounts.filter(a => a.account_type === 'demo').length,
    totalBalance: accounts.reduce((sum, a) => {
      const snapshot = accountSnapshots.get(a.id);
      const balance = snapshot?.balance ?? a.balance;
      return sum + parseFloat(balance || 0);
    }, 0),
    totalEquity: accounts.reduce((sum, a) => {
      const snapshot = accountSnapshots.get(a.id);
      const equity = snapshot?.equity ?? a.equity ?? a.balance;
      return sum + parseFloat(equity || 0);
    }, 0),
  };

  const hasActiveFilters = filters.account_type || filters.is_active !== undefined || searchTerm;
  const clearFilters = () => { setFilters({ account_type: '', is_active: undefined }); setSearchTerm(''); };

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="px-4 pt-5 pb-6" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-success)) 100%)'
        }}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {t('accounts.title')}
            </p>
            {wsConnected && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}>
                <Activity className="w-3 h-3" style={{ color: 'rgb(16, 185, 129)' }} />
                <span className="text-[10px] font-bold" style={{ color: 'rgb(16, 185, 129)' }}>LIVE</span>
              </div>
            )}
          </div>
          <p className="text-4xl font-black text-white leading-none font-mono">{stats.total}</p>
          <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Total accounts</p>

          <div className="grid grid-cols-3 gap-2 mt-4">
            {[
              { label: 'Live',    value: stats.live,    color: 'rgba(255,255,255,0.9)' },
              { label: 'Demo',    value: stats.demo,    color: 'rgba(255,255,255,0.9)' },
              { label: wsConnected ? 'Equity' : 'Balance', value: `$${((wsConnected ? stats.totalEquity : stats.totalBalance) / 1000).toFixed(1)}k`, color: 'rgba(255,255,255,0.9)' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-xl px-3 py-2.5 text-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <p className="text-sm font-black" style={{ color }}>{value}</p>
                <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Search + filters ... (keeping existing code) */}
        <div className="flex items-center gap-2 px-4 py-3 border-b"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadData()}
              placeholder="Account number or email…"
              className="w-full pl-9 pr-3 py-2 rounded-xl border text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            />
          </div>
          <button
            onClick={() => setShowFilters(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold relative"
            style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-black flex items-center justify-center text-white"
                style={{ backgroundColor: 'var(--accent-primary)' }}>
                {[filters.account_type, filters.is_active !== undefined, !!searchTerm].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Active filters (keeping existing code) */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto border-b" style={{ borderColor: 'var(--border-color)' }}>
            {searchTerm && (
              <div className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
                "{searchTerm}"
                <button onClick={() => setSearchTerm('')}><X className="w-2.5 h-2.5" /></button>
              </div>
            )}
            {filters.account_type && (
              <div className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
                {filters.account_type}
                <button onClick={() => setFilters(f => ({ ...f, account_type: '' }))}><X className="w-2.5 h-2.5" /></button>
              </div>
            )}
            {filters.is_active !== undefined && (
              <div className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
                {filters.is_active ? 'Active' : 'Inactive'}
                <button onClick={() => setFilters(f => ({ ...f, is_active: undefined }))}><X className="w-2.5 h-2.5" /></button>
              </div>
            )}
          </div>
        )}

        {/* Result count */}
        <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            {filteredAccounts.length} account{filteredAccounts.length !== 1 ? 's' : ''}
            {filteredAccounts.length !== accounts.length ? ` of ${accounts.length}` : ''}
          </p>
        </div>

        {/* Account list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <AlertCircle className="w-10 h-10 mb-3" style={{ color: 'var(--text-tertiary)' }} />
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No accounts found</p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="mt-2 text-xs font-semibold" style={{ color: 'var(--accent-primary)' }}>
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="px-4 pt-3 pb-8 space-y-2">
            {filteredAccounts.map(account => (
              <AccountCard
                key={account.id}
                account={account}
                snapshot={accountSnapshots.get(account.id)}
                onCreditDebit={handleOpenCreditDebit}
                onEdit={handleOpenEdit}
              />
            ))}
          </div>
        )}

        {/* Modals and drawers */}
        {showFilters && (
          <FilterDrawer
            filters={filters}
            setFilters={setFilters}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onSearch={loadData}
            onClose={() => setShowFilters(false)}
            onClear={clearFilters}
            hasActive={hasActiveFilters}
          />
        )}

        {showCreditModal && selectedAccount && (
          <CreditDebitModal
            account={selectedAccount}
            onClose={() => { setShowCreditModal(false); setSelectedAccount(null); }}
            onSubmit={async (amount, description) => {
              try {
                if (selectedAccount.type === 'credit') {
                  await creditAccount(selectedAccount.id, amount, description);
                  toast.success(`Account credited $${amount}`);
                } else {
                  await debitAccount(selectedAccount.id, amount, description);
                  toast.success(`Account debited $${amount}`);
                }
                setShowCreditModal(false);
                loadData();
              } catch { toast.error(`Failed to ${selectedAccount.type} account`); }
            }}
            t={t}
          />
        )}
        {showEditModal && selectedAccount && (
          <AccountEditModal
            account={selectedAccount}
            groups={groups}
            onClose={() => { setShowEditModal(false); setSelectedAccount(null); }}
            onSave={async (updates) => {
              try {
                await updateAccount(selectedAccount.id, updates);
                toast.success('Account updated successfully');
                setShowEditModal(false);
                loadData();
              } catch { toast.error('Failed to update account'); }
            }}
            t={t}
          />
        )}

        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold">{t('accounts.title')}</h1>
            {wsConnected && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                <Activity className="w-3.5 h-3.5" style={{ color: 'rgb(16, 185, 129)' }} />
                <span className="text-xs font-bold" style={{ color: 'rgb(16, 185, 129)' }}>LIVE DATA</span>
              </div>
            )}
          </div>
          <p className="text-[var(--text-secondary)]">Manage trading accounts, balances, and settings</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          <p className="text-sm text-[var(--text-secondary)] mb-1">Total Accounts</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          <p className="text-sm text-[var(--text-secondary)] mb-1">Live Accounts</p>
          <p className="text-2xl font-bold text-[var(--accent-success)]">{stats.live}</p>
        </div>
        <div className="p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          <p className="text-sm text-[var(--text-secondary)] mb-1">Demo Accounts</p>
          <p className="text-2xl font-bold text-[var(--accent-info)]">{stats.demo}</p>
        </div>
        <div className="p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-[var(--text-secondary)]">{wsConnected ? 'Total Equity' : 'Total Balance'}</p>
            {wsConnected && <Activity className="w-3.5 h-3.5 text-[var(--accent-success)]" />}
          </div>
          <p className="text-2xl font-bold">
            ${(wsConnected ? stats.totalEquity : stats.totalBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-tertiary)]" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by account number or email..."
              className="w-full pl-10 pr-4 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none"
            />
          </div>
        </form>
        <select
          value={filters.account_type}
          onChange={e => setFilters({ ...filters, account_type: e.target.value })}
          className="px-4 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none"
        >
          <option value="">All Types</option>
          <option value="live">Live Only</option>
          <option value="demo">Demo Only</option>
        </select>
        <select
          value={filters.is_active === undefined ? '' : filters.is_active}
          onChange={e => setFilters({ ...filters, is_active: e.target.value === '' ? undefined : e.target.value === 'true' })}
          className="px-4 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none"
        >
          <option value="">All Status</option>
          <option value="true">Active Only</option>
          <option value="false">Inactive Only</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
          <table className="w-full">
            <thead>
              <tr className="bg-[var(--bg-secondary)] border-b border-[var(--border-color)]">
                <th className="px-4 py-3 text-left text-sm font-semibold">Account</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Client</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Balance</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1.5">
                    Equity
                    {wsConnected && <Activity className="w-3 h-3 text-[var(--accent-success)]" />}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1.5">
                    P&L
                    {wsConnected && <Activity className="w-3 h-3 text-[var(--accent-success)]" />}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Margin</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-[var(--text-secondary)]">No accounts found</td>
                </tr>
              ) : filteredAccounts.map(account => {
                const snapshot = accountSnapshots.get(account.id);
                const balance = snapshot?.balance ?? account.balance;
                const equity = snapshot?.equity ?? account.equity ?? account.balance;
                const margin_level = snapshot?.margin_level;
                const margin_used = snapshot?.margin_used ?? 0;
                
                // Calculate total P&L from positions
                const totalPnL = snapshot?.positions?.reduce((sum, pos) => sum + parseFloat(pos.unrealized_pnl || 0), 0) || 0;
                const positionsCount = snapshot?.positions?.length || 0;
                
                const isLive = !!snapshot;
                
                return (
                  <tr
                    key={account.id}
                    className="border-b border-[var(--border-color)] hover:bg-[var(--bg-secondary)] transition-smooth"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isLive && (
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        )}
                        <div>
                          <p className="font-mono font-semibold">{account.account_number}</p>
                          <p className="text-xs text-[var(--text-secondary)]">{account.group_name || 'Standard'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{account.email}</p>
                      <p className="text-xs text-[var(--text-secondary)]">{account.first_name} {account.last_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        account.account_type === 'live'
                          ? 'bg-[var(--accent-success)]/20 text-[var(--accent-success)]'
                          : 'bg-[var(--accent-info)]/20 text-[var(--accent-info)]'
                      }`}>
                        {account.account_type === 'live' ? 'Live' : 'Demo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-mono font-semibold">
                        ${parseFloat(balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className={`font-mono font-semibold ${
                        parseFloat(equity) >= parseFloat(balance) 
                          ? 'text-[var(--accent-success)]' 
                          : 'text-[var(--accent-danger)]'
                      }`}>
                        ${parseFloat(equity || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      {isLive && parseFloat(equity) !== parseFloat(balance) && (
                        <p className="text-xs font-mono" style={{ 
                          color: parseFloat(equity) >= parseFloat(balance) 
                            ? 'var(--accent-success)' 
                            : 'var(--accent-danger)' 
                        }}>
                          {parseFloat(equity) >= parseFloat(balance) ? '+' : ''}
                          ${(parseFloat(equity) - parseFloat(balance)).toFixed(2)}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isLive && positionsCount > 0 ? (
                        <div>
                          <p className={`font-mono font-bold text-sm ${
                            totalPnL >= 0 
                              ? 'text-[var(--accent-success)]' 
                              : 'text-[var(--accent-danger)]'
                          }`}>
                            {totalPnL >= 0 ? '+' : ''}${totalPnL.toFixed(2)}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                            {positionsCount} {positionsCount === 1 ? 'position' : 'positions'}
                          </p>
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--text-tertiary)]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {margin_used > 0 ? (
                        <div>
                          <p className="font-mono text-sm">
                            ${parseFloat(margin_used).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          {margin_level && (
                            <p className={`text-xs font-mono font-semibold ${
                              margin_level < 100 
                                ? 'text-[var(--accent-danger)]' 
                                : margin_level < 200 
                                ? 'text-[var(--accent-warning)]' 
                                : 'text-[var(--accent-success)]'
                            }`}>
                              {margin_level.toFixed(2)}%
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--text-tertiary)]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleToggleActive(account)}
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            account.is_active
                              ? 'bg-[var(--accent-success)]/20 text-[var(--accent-success)]'
                              : 'bg-[var(--text-tertiary)]/20 text-[var(--text-tertiary)]'
                          }`}
                        >
                          {account.is_active ? 'Active' : 'Inactive'}
                        </button>
                        {Boolean(account.read_only) && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-[var(--accent-warning)]/20 text-[var(--accent-warning)] flex items-center gap-1">
                            <Lock className="w-3 h-3" /> Read-Only
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleOpenCreditDebit(account, 'credit')}
                          className="p-2 hover:bg-[var(--accent-success)]/20 rounded-lg transition-smooth"
                          title="Credit"
                        >
                          <TrendingUp className="w-4 h-4 text-[var(--accent-success)]" />
                        </button>
                        <button
                          onClick={() => handleOpenCreditDebit(account, 'debit')}
                          className="p-2 hover:bg-[var(--accent-danger)]/20 rounded-lg transition-smooth"
                          title="Debit"
                        >
                          <TrendingDown className="w-4 h-4 text-[var(--accent-danger)]" />
                        </button>
                        <button
                          onClick={() => handleOpenEdit(account)}
                          className="p-2 hover:bg-[var(--bg-tertiary)] rounded-lg transition-smooth"
                          title="Settings"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreditModal && selectedAccount && (
        <CreditDebitModal
          account={selectedAccount}
          onClose={() => { setShowCreditModal(false); setSelectedAccount(null); }}
          onSubmit={async (amount, description) => {
            try {
              if (selectedAccount.type === 'credit') {
                await creditAccount(selectedAccount.id, amount, description);
                toast.success(`Account credited $${amount}`);
              } else {
                await debitAccount(selectedAccount.id, amount, description);
                toast.success(`Account debited $${amount}`);
              }
              setShowCreditModal(false);
              loadData();
            } catch { toast.error(`Failed to ${selectedAccount.type} account`); }
          }}
          t={t}
        />
      )}
      {showEditModal && selectedAccount && (
        <AccountEditModal
          account={selectedAccount}
          groups={groups}
          onClose={() => { setShowEditModal(false); setSelectedAccount(null); }}
          onSave={async (updates) => {
            try {
              await updateAccount(selectedAccount.id, updates);
              toast.success('Account updated successfully');
              setShowEditModal(false);
              loadData();
            } catch { toast.error('Failed to update account'); }
          }}
          t={t}
        />
      )}
    </div>
  );
}

// Modals remain unchanged
function CreditDebitModal({ account, onClose, onSubmit, t }) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const isCredit = account.type === 'credit';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { toast.error('Please enter a valid amount'); return; }
    onSubmit(parseFloat(amount), description);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-primary)] rounded-2xl border border-[var(--border-color)] w-full max-w-md">
        <div className="border-b border-[var(--border-color)] p-6">
          <h2 className="text-xl font-bold">{isCredit ? 'Credit Account' : 'Debit Account'}</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {account.account_number} — Current Balance: ${parseFloat(account.balance).toFixed(2)}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Amount ($)</label>
            <input
              type="number" step="0.01" min="0.01" value={amount}
              onChange={e => setAmount(e.target.value)}
              className="w-full px-4 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none font-mono"
              placeholder="0.00" required autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={description} onChange={e => setDescription(e.target.value)}
              className="w-full px-4 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none"
              rows="3" placeholder="Reason for adjustment..."
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose}
              className="flex-1 px-6 py-3 bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-smooth">
              Cancel
            </button>
            <button type="submit"
              className={`flex-1 px-6 py-3 text-white rounded-lg transition-smooth ${
                isCredit ? 'bg-[var(--accent-success)] hover:bg-[var(--accent-success)]/90' : 'bg-[var(--accent-danger)] hover:bg-[var(--accent-danger)]/90'
              }`}>
              {isCredit ? 'Credit' : 'Debit'} ${amount || '0.00'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AccountEditModal({ account, groups, onClose, onSave, t }) {
  const [formData, setFormData] = useState({
    group_id:  account.group_id || 1,
    is_active: Boolean(account.is_active),
    read_only: Boolean(account.read_only),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, is_active: formData.is_active ? 1 : 0, read_only: formData.read_only ? 1 : 0 });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-primary)] rounded-2xl border border-[var(--border-color)] w-full max-w-md">
        <div className="border-b border-[var(--border-color)] p-6">
          <h2 className="text-xl font-bold">Account Settings</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1">{account.account_number}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Account Group</label>
            <select
              value={formData.group_id}
              onChange={e => setFormData({ ...formData, group_id: parseInt(e.target.value) })}
              className="w-full px-4 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none"
            >
              {groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={formData.is_active}
                onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 rounded border-2 border-[var(--border-color)] text-[var(--accent-primary)]" />
              <span className="text-sm font-medium">Account Active</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={formData.read_only}
                onChange={e => setFormData({ ...formData, read_only: e.target.checked })}
                className="w-5 h-5 rounded border-2 border-[var(--border-color)] text-[var(--accent-primary)]" />
              <span className="text-sm font-medium">Read-Only Mode</span>
            </label>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose}
              className="flex-1 px-6 py-3 bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-smooth">
              Cancel
            </button>
            <button type="submit"
              className="flex-1 px-6 py-3 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-hover)] transition-smooth">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}